import {
  Paper,
  Typography,
  Box,
} from '@mui/material';

/**
 * TermsOfUseContent - Conteúdo dos Termos de Uso e Política de Privacidade
 * 
 * Reutilizável em modal ou página completa
 * Conforme Lei Geral de Proteção de Dados Pessoais (LGPD - Lei 13.709/2018)
 */
export default function TermsOfUseContent() {
  // Helper para exibir email de forma protegida
  const renderEmail = (email) => {
    return email
      .split('')
      .map(char => `&#${char.charCodeAt(0)};`)
      .join('');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: { xs: 2, sm: 3 },
        backgroundColor: '#fafafa',
      }}
    >
      {/* Cabeçalho */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="h6" component="h1" sx={{ marginBottom: 1, fontWeight: 600 }}>
          Termos de Uso e Política de Privacidade
        </Typography>
        <Typography variant="caption" sx={{ color: '#757575' }}>
          Última atualização: Outubro 2026
        </Typography>
      </Box>

      {/* Introdução */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          1. Introdução
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1 }}>
          Esta Política de Privacidade e Termos de Uso descrevem como a <strong>Blasoft Agendário</strong> (CNPJ 68.174.761/0001-01) ("Empresa", "nós" ou "nosso") coleta, usa, compartilha e protege suas informações pessoais através da plataforma <strong>Agendário</strong> ("Serviço").
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Esta política é oferecida de acordo com a Lei Geral de Proteção de Dados Pessoais - <strong>Lei nº 13.709 de 14 de agosto de 2018</strong> (LGPD) e legislação complementar.
        </Typography>
      </Box>

      {/* 2. Definições */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          2. Definições Importantes
        </Typography>
        <Box sx={{ marginBottom: 2, paddingLeft: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Dados Pessoais:</strong> Qualquer informação relacionada à pessoa natural identificada ou identificável.
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Titular:</strong> Pessoa natural a quem se referem os dados pessoais que são objeto de tratamento.
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Controlador:</strong> Pessoa natural ou jurídica que decide sobre o tratamento de dados pessoais. Neste caso, a Blasoft Agendário.
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Operador:</strong> Pessoa natural ou jurídica que realiza o tratamento de dados em nome do controlador.
          </Typography>
          <Typography variant="body2">
            <strong>Tratamento:</strong> Toda operação realizada com dados pessoais (coleta, recebimento, classificação, utilização, acesso, armazenamento, entre outras).
          </Typography>
        </Box>
      </Box>

      {/* 3. Coleta de Dados */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          3. Que Dados Coletamos
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 2 }}>
          Coletamos dados pessoais que você fornece voluntariamente ou que obtemos através de sua interação com nosso Serviço:
        </Typography>
        <Box sx={{ paddingLeft: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Dados de Identificação:</strong> Nome, email, telefone, CPF ou CNPJ, endereço
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Dados de Cobrança:</strong> Nome ou razão social e CPF ou CNPJ informados nas compras feitas no painel (enviados ao intermediador de pagamento para gerar a cobrança)
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Dados Profissionais:</strong> Especialidade, CRO/CRMV, horários de atendimento, localização do estabelecimento
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Dados de Agendamento:</strong> Datas, horários, serviços solicitados, histórico de consultas
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Dados de Pagamento:</strong> Informações de transações (NÃO armazenamos dados brutos de cartão)
          </Typography>
          <Typography variant="body2">
            <strong>Dados de Uso:</strong> IP, navegador, páginas visitadas, duração das sessões (via cookies)
          </Typography>
        </Box>
      </Box>

      {/* 4. Finalidade do Tratamento */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          4. Para Que Usamos Seus Dados
        </Typography>
        <Box sx={{ paddingLeft: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Manutenção da Conta:</strong> Criar, manter e gerenciar sua conta de usuário
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Prestação do Serviço:</strong> Processar agendamentos e cancelamentos
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Comunicações:</strong> Notificações de agendamentos, lembretes, confirmações (email/SMS)
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Segurança:</strong> Detectar fraudes, ataques e proteger a plataforma
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Conformidade Legal:</strong> Cumprir obrigações regulatórias e contratuais
          </Typography>
          <Typography variant="body2">
            <strong>Melhorias:</strong> Analisar uso e melhorar recursos (dados anônimos)
          </Typography>
        </Box>
      </Box>

      {/* 5. Base Legal */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          5. Base Legal para o Tratamento
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          O tratamento de seus dados é realizado com base em: <strong>(i) consentimento</strong> expresso por você; <strong>(ii) execução de contrato</strong> que você integre; <strong>(iii) obrigação legal</strong> ou regulatória; e <strong>(iv) interesse legítimo</strong> da empresa em operar de forma segura e eficiente.
        </Typography>
      </Box>

      {/* 6. Compartilhamento de Dados */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          6. Compartilhamento de Dados
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 2 }}>
          Seus dados podem ser compartilhados com:
        </Typography>
        <Box sx={{ paddingLeft: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Prestadores de Serviço:</strong> Fornecedores de infraestrutura, email e pagamento (sob NDA)
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Autoridades Públicas:</strong> Quando exigido por lei ou ordem judicial
          </Typography>
          <Typography variant="body2">
            <strong>Profissionais de Saúde:</strong> Clínicas/consultórios que você utiliza
          </Typography>
        </Box>
      </Box>

      {/* 7. Retenção de Dados */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          7. Quanto Tempo Mantemos seus Dados
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Mantemos seus dados enquanto sua conta estiver ativa ou conforme necessário para cumprir obrigações legais. Após exclusão da conta, dados são armazenados por 90 dias em backup antes de serem removidos permanentemente. Dados de transações são mantidos por 5 anos para conformidade fiscal.
        </Typography>
      </Box>

      {/* 8. Segurança */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          8. Segurança de Dados
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Implementamos medidas técnicas e organizacionais para proteger seus dados contra acesso não autorizado, incluindo: criptografia end-to-end, firewalls, autenticação de dois fatores e auditorias de segurança regulares.
        </Typography>
      </Box>

      {/* 9. Seus Direitos */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          9. Seus Direitos Conforme a LGPD
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 2 }}>
          De acordo com a Lei Geral de Proteção de Dados Pessoais, você possui os seguintes direitos:
        </Typography>
        <Box sx={{ paddingLeft: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Acesso:</strong> Solicitar informações sobre quais dados seus mantemos
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Correção:</strong> Alterar dados incorretos ou desatualizados
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Exclusão:</strong> Solicitar remoção de seus dados (direito ao esquecimento)
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            <strong>Portabilidade:</strong> Obter dados em formato estruturado
          </Typography>
          <Typography variant="body2">
            <strong>Oposição:</strong> Objetar o processamento de dados para certos fins
          </Typography>
        </Box>
      </Box>

      {/* 10. Contato DPO */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          10. Encarregado de Proteção de Dados (DPO)
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1 }}>
          Para exercer seus direitos ou fazer reclamações sobre tratamento de dados, entre em contato conosco:
        </Typography>
        <Box sx={{ paddingLeft: 2, backgroundColor: '#fff', padding: 2, borderRadius: 1, marginTop: 1 }}>
          <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
            <strong>Email:</strong>{' '}
            <span dangerouslySetInnerHTML={{ __html: renderEmail('dpo@blasoft.dev') }} />
          </Typography>
          <Typography variant="body2">
            <strong>Telefone:</strong>{' '}
            <span data-phone="+55 41 98839-3728">(41) 98839-3728</span>
          </Typography>
        </Box>
      </Box>

      {/* 11. Cookies */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          11. Política de Cookies
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Usamos cookies para melhorar sua experiência, manter sessões ativas e analisar uso. Cookies são pequenos arquivos armazenados em seu navegador. Você pode desabilitá-los nas configurações do navegador, mas alguns recursos podem não funcionar corretamente.
        </Typography>
      </Box>

      {/* 12. Alterações na Política */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          12. Alterações nesta Política
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Podemos atualizar esta política periodicamente. Alterações significativas serão comunicadas via email ou notificação na plataforma.
        </Typography>
      </Box>

      {/* 13. Lei Aplicável */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
          13. Lei Aplicável e Foro
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Esta política é regida pelas leis brasileiras, especialmente pela Lei Geral de Proteção de Dados Pessoais (Lei 13.709/2018). Qualquer disputa será resolvida nos tribunais da Justiça Estadual ou Federal, conforme aplicável.
        </Typography>
      </Box>

      {/* Rodapé */}
      <Box
        sx={{
          paddingTop: 3,
          borderTop: '1px solid #ddd',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: '#999' }}>
          Versão 1.2 | Outubro 2026 | Este documento foi elaborado de acordo com a Lei Geral de Proteção de Dados Pessoais (LGPD - Lei nº 13.709/2018).
        </Typography>
      </Box>
    </Paper>
  );
}
